/**
 * PM Command Center - Option D: "Mission Control + Inbox" Hybrid
 *
 * Top: Mission Control bar with clickable status badges (dashboard + filters)
 * Left: Smart filtered project list
 * Right: Full project workspace with tabs
 */

import { useState, useEffect, useMemo } from "react";

// Mock data for prototype (same as Option C)
const MOCK_PROJECTS = [
  {
    id: "1",
    project_id: "PG-2401",
    customer_name: "Procter & Gamble",
    description: "Tide Pods Holiday Packaging - Q1 2025 refresh with new seasonal graphics",
    status: "intake",
    ship_date: new Date().toISOString().split('T')[0],
    sales_rep: "Jennifer Wilson",
    quote_number: "Q-24-1234",
    qty_shrinks: 12,
    qty_cartons: 8,
    qty_labels: 24,
    questions_pending: 0,
    art_files: [],
    notes: "Rush order - customer needs by end of month",
    created_at: "2024-12-06T10:00:00Z",
  },
  {
    id: "2",
    project_id: "JJ-2402",
    customer_name: "Johnson & Johnson",
    description: "Band-Aid Box Redesign - New eco-friendly packaging",
    status: "intake",
    ship_date: new Date().toISOString().split('T')[0],
    sales_rep: "Mike Stevens",
    quote_number: "Q-24-1235",
    qty_shrinks: 0,
    qty_cartons: 15,
    qty_labels: 30,
    questions_pending: 0,
    art_files: [],
    notes: "",
    created_at: "2024-12-06T11:00:00Z",
  },
  {
    id: "3",
    project_id: "KR-2398",
    customer_name: "Kraft Heinz",
    description: "Mac & Cheese Holiday Edition - Seasonal sleeve design",
    status: "prep",
    ship_date: "2024-12-15",
    sales_rep: "Mike Stevens",
    quote_number: "Q-24-1230",
    qty_shrinks: 8,
    qty_cartons: 12,
    qty_labels: 0,
    questions_pending: 2,
    art_files: [
      { name: "KR_artwork_v2.ai", size: "4.2 MB", uploaded: "2024-12-05" },
      { name: "KR_dieline.pdf", size: "1.1 MB", uploaded: "2024-12-05" },
      { name: "KR_specs.docx", size: "245 KB", uploaded: "2024-12-05" },
    ],
    questions: [
      { id: 1, text: "Is the spot color PMS 286 or PMS 286C?", to: "Mike Stevens", asked: "2024-12-06", answered: null },
      { id: 2, text: "Confirm qty: 12 shrinks or 12 each of shrink+carton?", to: "Mike Stevens", asked: "2024-12-06", answered: null },
    ],
    notes: "Customer very particular about color matching",
    created_at: "2024-12-04T09:00:00Z",
  },
  {
    id: "4",
    project_id: "CL-2399",
    customer_name: "Colgate-Palmolive",
    description: "Toothpaste Tube Sleeves - New mint variant",
    status: "prep",
    ship_date: "2024-12-18",
    sales_rep: "Jennifer Wilson",
    quote_number: "Q-24-1231",
    qty_shrinks: 20,
    qty_cartons: 0,
    qty_labels: 40,
    questions_pending: 0,
    art_files: [
      { name: "CL_sleeve_artwork.ai", size: "3.8 MB", uploaded: "2024-12-06" },
    ],
    notes: "Analyzing art files...",
    created_at: "2024-12-05T14:00:00Z",
  },
  {
    id: "5",
    project_id: "UN-2400",
    customer_name: "Unilever",
    description: "Dove Body Wash Labels - Spring collection",
    status: "prep",
    ship_date: "2024-12-20",
    sales_rep: "Jennifer Wilson",
    quote_number: "Q-24-1232",
    qty_shrinks: 0,
    qty_cartons: 6,
    qty_labels: 50,
    questions_pending: 0,
    art_files: [
      { name: "UN_dove_labels.ai", size: "5.1 MB", uploaded: "2024-12-04" },
      { name: "UN_specs.pdf", size: "890 KB", uploaded: "2024-12-04" },
    ],
    notes: "Ready for work order creation",
    created_at: "2024-12-03T10:00:00Z",
  },
  {
    id: "6",
    project_id: "NE-2395",
    customer_name: "Nestle",
    description: "Coffee Bag Redesign - Premium line",
    status: "production",
    ship_date: "2024-12-22",
    sales_rep: "Mike Stevens",
    quote_number: "Q-24-1225",
    qty_shrinks: 0,
    qty_cartons: 10,
    qty_labels: 0,
    progress: 45,
    work_order_created: true,
    notes: "Production on track",
    created_at: "2024-11-28T09:00:00Z",
  },
  {
    id: "7",
    project_id: "PG-2396",
    customer_name: "Procter & Gamble",
    description: "Pampers Display Stand - Retail promotion",
    status: "production",
    ship_date: "2024-12-19",
    sales_rep: "Jennifer Wilson",
    quote_number: "Q-24-1226",
    qty_shrinks: 4,
    qty_cartons: 0,
    qty_labels: 8,
    progress: 60,
    work_order_created: true,
    notes: "",
    created_at: "2024-11-29T11:00:00Z",
  },
  {
    id: "8",
    project_id: "UN-2397",
    customer_name: "Unilever",
    description: "Axe Body Spray Cartons - Limited edition",
    status: "proofing",
    ship_date: "2024-12-16",
    sales_rep: "Mike Stevens",
    quote_number: "Q-24-1227",
    qty_shrinks: 0,
    qty_cartons: 18,
    qty_labels: 0,
    proof_status: "awaiting_approval",
    proof_sent: "2024-12-05",
    notes: "Proof sent to client, awaiting feedback",
    created_at: "2024-11-25T10:00:00Z",
  },
  {
    id: "9",
    project_id: "KR-2394",
    customer_name: "Kraft Heinz",
    description: "Ketchup Bottle Labels - Standard reorder",
    status: "production",
    ship_date: "2024-12-21",
    sales_rep: "Jennifer Wilson",
    quote_number: "Q-24-1224",
    qty_shrinks: 0,
    qty_cartons: 0,
    qty_labels: 100,
    progress: 30,
    work_order_created: true,
    notes: "",
    created_at: "2024-11-27T09:00:00Z",
  },
  {
    id: "10",
    project_id: "NE-2393",
    customer_name: "Nestle",
    description: "Hot Cocoa Box - Winter seasonal",
    status: "proofing",
    ship_date: "2024-12-17",
    sales_rep: "Mike Stevens",
    quote_number: "Q-24-1223",
    qty_shrinks: 6,
    qty_cartons: 12,
    qty_labels: 0,
    proof_status: "awaiting_approval",
    proof_sent: "2024-12-04",
    notes: "",
    created_at: "2024-11-24T10:00:00Z",
  },
];

// Status configuration
const STATUS_CONFIG = {
  intake: { label: "Intake", color: "bg-blue-500", hoverColor: "hover:bg-blue-600", icon: "📥" },
  prep: { label: "Prep", color: "bg-yellow-500", hoverColor: "hover:bg-yellow-600", icon: "✍️" },
  production: { label: "Production", color: "bg-green-500", hoverColor: "hover:bg-green-600", icon: "🏭" },
  proofing: { label: "Proofing", color: "bg-purple-500", hoverColor: "hover:bg-purple-600", icon: "📄" },
  complete: { label: "Complete", color: "bg-slate-200", hoverColor: "hover:bg-gray-200", icon: "✅" },
};

// Workspace tabs
const WORKSPACE_TABS = [
  { id: "overview", label: "Overview" },
  { id: "art_files", label: "Art Files" },
  { id: "questions", label: "Questions" },
  { id: "work_order", label: "Work Order" },
  { id: "production", label: "Production" },
  { id: "proofs", label: "Proofs" },
  { id: "notes", label: "Notes" },
];

export default function PMCenterOptionD({ onBackToApp }) {
  const [projects] = useState(MOCK_PROJECTS);
  const [activeFilter, setActiveFilter] = useState(null); // null = all, or "ships_today", "needs_answers", status name
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");

  // Calculate stats for mission control
  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return {
      shipsToday: projects.filter(p => p.ship_date === today).length,
      needsAnswers: projects.filter(p => p.questions_pending > 0).length,
      intake: projects.filter(p => p.status === "intake").length,
      prep: projects.filter(p => p.status === "prep").length,
      production: projects.filter(p => p.status === "production").length,
      proofing: projects.filter(p => p.status === "proofing").length,
      atRisk: projects.filter(p => {
        const days = getDaysUntilShip(p.ship_date);
        return days !== null && days <= 3 && p.status !== "complete";
      }).length,
    };
  }, [projects]);

  // Filter projects
  const filteredProjects = useMemo(() => {
    let filtered = [...projects];
    const today = new Date().toISOString().split('T')[0];

    // Apply active filter
    switch (activeFilter) {
      case "ships_today":
        filtered = filtered.filter(p => p.ship_date === today);
        break;
      case "needs_answers":
        filtered = filtered.filter(p => p.questions_pending > 0);
        break;
      case "at_risk":
        filtered = filtered.filter(p => {
          const days = getDaysUntilShip(p.ship_date);
          return days !== null && days <= 3 && p.status !== "complete";
        });
        break;
      case "intake":
      case "prep":
      case "production":
      case "proofing":
      case "complete":
        filtered = filtered.filter(p => p.status === activeFilter);
        break;
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.project_id.toLowerCase().includes(query) ||
        p.customer_name.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query)
      );
    }

    // Sort by ship date
    filtered.sort((a, b) => new Date(a.ship_date) - new Date(b.ship_date));

    return filtered;
  }, [projects, activeFilter, searchQuery]);

  // Select first project by default
  useEffect(() => {
    if (filteredProjects.length > 0 && !selectedProject) {
      setSelectedProject(filteredProjects[0]);
    }
  }, [filteredProjects]);

  // When filter changes, select first matching project
  useEffect(() => {
    if (filteredProjects.length > 0) {
      const currentStillVisible = filteredProjects.find(p => p.id === selectedProject?.id);
      if (!currentStillVisible) {
        setSelectedProject(filteredProjects[0]);
      }
    }
  }, [activeFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  function getDaysUntilShip(dateStr) {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ship = new Date(dateStr);
    ship.setHours(0, 0, 0, 0);
    return Math.ceil((ship - today) / (1000 * 60 * 60 * 24));
  }

  const getUrgencyIndicator = (project) => {
    const days = getDaysUntilShip(project.ship_date);
    if (days === null) return null;
    if (days <= 0) return { icon: "🔴", label: "TODAY", class: "text-red-400" };
    if (days === 1) return { icon: "🔴", label: "Tomorrow", class: "text-red-400" };
    if (days <= 3) return { icon: "🟡", label: `${days}d`, class: "text-yellow-400" };
    return { icon: "🟢", label: `${days}d`, class: "text-gray-500" };
  };

  // Mission Control badge component
  const MissionBadge = ({ id, icon, count, label, color, isUrgent = false }) => {
    const isActive = activeFilter === id;
    return (
      <button
        onClick={() => setActiveFilter(isActive ? null : id)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
          isActive
            ? `${color} ring-2 ring-white/50 scale-105`
            : `bg-slate-200 hover:bg-slate-100 ${isUrgent && count > 0 ? "animate-pulse" : ""}`
        }`}
      >
        <span className="text-lg">{icon}</span>
        <div className="text-left">
          <div className={`text-xl font-bold ${isUrgent && count > 0 ? "text-red-400" : ""}`}>
            {count}
          </div>
          <div className="text-xs text-gray-700 whitespace-nowrap">{label}</div>
        </div>
      </button>
    );
  };

  // Render project list item
  const renderProjectItem = (project) => {
    const isSelected = selectedProject?.id === project.id;
    const urgency = getUrgencyIndicator(project);
    const status = STATUS_CONFIG[project.status];

    return (
      <div
        key={project.id}
        onClick={() => {
          setSelectedProject(project);
          setActiveTab("overview");
        }}
        className={`p-3 cursor-pointer border-b border-white/5 transition-all ${
          isSelected
            ? "bg-purple-600/20 border-l-4 border-l-purple-500"
            : "hover:bg-slate-200 border-l-4 border-l-transparent"
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {urgency && (
                <span className={urgency.class} title={urgency.label}>
                  {urgency.icon}
                </span>
              )}
              <span className="font-mono text-xs text-purple-600">
                {project.project_id}
              </span>
            </div>
            <div className="text-sm font-medium mt-0.5 truncate">
              {project.customer_name}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {project.description}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className={`px-1.5 py-0.5 rounded text-xs ${status.color}`}>
                {status.label}
              </span>
              {project.questions_pending > 0 && (
                <span className="text-xs text-yellow-400 flex items-center gap-1">
                  <span>⚠️</span> {project.questions_pending}
                </span>
              )}
              {project.progress !== undefined && (
                <div className="flex items-center gap-1">
                  <div className="w-12 h-1.5 bg-gray-200 rounded-full">
                    <div
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500">{project.progress}%</span>
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className={`text-sm font-medium ${urgency?.class || "text-gray-500"}`}>
              {formatDate(project.ship_date)}
            </div>
            {urgency && (
              <div className={`text-xs ${urgency.class}`}>
                {urgency.label}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render workspace content based on active tab
  const renderWorkspaceContent = () => {
    if (!selectedProject) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          Select a project to view details
        </div>
      );
    }

    switch (activeTab) {
      case "overview":
        return (
          <div className="p-4 space-y-4">
            {/* Status & Key Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-200 rounded-lg p-3 border border-white/5">
                <div className="text-xs text-gray-500">Status</div>
                <div className={`inline-block px-2 py-1 rounded text-sm mt-1 ${STATUS_CONFIG[selectedProject.status]?.color}`}>
                  {STATUS_CONFIG[selectedProject.status]?.label}
                </div>
              </div>
              <div className="bg-gray-200 rounded-lg p-3 border border-white/5">
                <div className="text-xs text-gray-500">Ship Date</div>
                <div className="text-sm mt-1 font-medium">{formatDate(selectedProject.ship_date)}</div>
                {getUrgencyIndicator(selectedProject) && (
                  <div className={`text-xs ${getUrgencyIndicator(selectedProject).class}`}>
                    {getUrgencyIndicator(selectedProject).label}
                  </div>
                )}
              </div>
              <div className="bg-gray-200 rounded-lg p-3 border border-white/5">
                <div className="text-xs text-gray-500">Sales Rep</div>
                <div className="text-sm mt-1">{selectedProject.sales_rep}</div>
              </div>
              <div className="bg-gray-200 rounded-lg p-3 border border-white/5">
                <div className="text-xs text-gray-500">Quote</div>
                <div className="text-sm mt-1 font-mono">{selectedProject.quote_number}</div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-gray-200 rounded-lg p-4 border border-white/5">
              <div className="text-xs text-gray-500 mb-2">Description</div>
              <div className="text-sm">{selectedProject.description}</div>
            </div>

            {/* Quantities */}
            <div className="bg-gray-200 rounded-lg p-4 border border-white/5">
              <div className="text-xs text-gray-500 mb-3">Quantities</div>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-400">{selectedProject.qty_shrinks}</div>
                  <div className="text-xs text-gray-500">Shrinks</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-400">{selectedProject.qty_cartons}</div>
                  <div className="text-xs text-gray-500">Cartons</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">{selectedProject.qty_labels}</div>
                  <div className="text-xs text-gray-500">Labels</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {selectedProject.qty_shrinks + selectedProject.qty_cartons + selectedProject.qty_labels}
                  </div>
                  <div className="text-xs text-gray-500">Total</div>
                </div>
              </div>
            </div>

            {/* Alerts if any */}
            {selectedProject.questions_pending > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-yellow-400">
                  <span>⚠️</span>
                  <span className="font-medium">{selectedProject.questions_pending} questions pending</span>
                </div>
                <button
                  onClick={() => setActiveTab("questions")}
                  className="mt-2 text-sm text-yellow-400 hover:text-yellow-600 underline"
                >
                  View questions →
                </button>
              </div>
            )}

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3 pt-2">
              <button className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors">
                Create Work Order
              </button>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors">
                Ask Question
              </button>
              <button className="px-4 py-2 bg-gray-200 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors">
                Add Note
              </button>
              <button className="px-4 py-2 bg-gray-200 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors">
                Upload Files
              </button>
            </div>
          </div>
        );

      case "art_files":
        return (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Art Files</h3>
              <button className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors">
                + Upload Files
              </button>
            </div>
            {selectedProject.art_files?.length > 0 ? (
              <div className="space-y-2">
                {selectedProject.art_files.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-200 rounded-lg border border-white/5">
                    <span className="text-2xl">📄</span>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{file.name}</div>
                      <div className="text-xs text-gray-500">{file.size} • Uploaded {file.uploaded}</div>
                    </div>
                    <button className="px-3 py-1.5 bg-gray-200 hover:bg-gray-200 rounded text-xs transition-colors">
                      Preview
                    </button>
                    <button className="px-3 py-1.5 bg-gray-200 hover:bg-gray-200 rounded text-xs transition-colors">
                      Download
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 bg-gray-200 rounded-lg border-2 border-dashed border-gray-200">
                <div className="text-4xl mb-2">📁</div>
                <div>No art files uploaded yet</div>
                <button className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors">
                  Upload Files
                </button>
              </div>
            )}
          </div>
        );

      case "questions":
        return (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Questions for Sales</h3>
              <button className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors">
                + Ask Question
              </button>
            </div>
            {selectedProject.questions?.length > 0 ? (
              <div className="space-y-3">
                {selectedProject.questions.map((q) => (
                  <div key={q.id} className="p-4 bg-gray-200 rounded-lg border-l-4 border-yellow-500">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-yellow-400 text-sm mb-1">
                          <span>❓</span>
                          <span>Pending</span>
                        </div>
                        <div className="text-sm">{q.text}</div>
                        <div className="text-xs text-gray-500 mt-2">
                          To: {q.to} • Asked {q.asked}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button className="px-3 py-1.5 bg-gray-200 hover:bg-gray-200 rounded text-xs transition-colors">
                          Resend
                        </button>
                        <button className="px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded text-xs transition-colors">
                          Got Answer
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 bg-gray-200 rounded-lg">
                <div className="text-4xl mb-2">✅</div>
                <div>No pending questions</div>
              </div>
            )}
          </div>
        );

      case "work_order":
        return (
          <div className="p-4 space-y-4">
            <h3 className="font-semibold">Work Order</h3>
            {selectedProject.work_order_created ? (
              <div className="space-y-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-400 font-medium">
                    <span>✅</span>
                    <span>Work order created</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors">
                    View Work Order
                  </button>
                  <button className="px-4 py-2 bg-gray-200 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors">
                    Edit
                  </button>
                  <button className="px-4 py-2 bg-gray-200 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors">
                    Print
                  </button>
                  <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg text-sm font-medium transition-colors">
                    Create Change Order
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-200 rounded-lg border border-white/5">
                <div className="text-4xl mb-2">📋</div>
                <div className="text-gray-500 mb-4">No work order created yet</div>
                <button className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors">
                  Create Work Order
                </button>
              </div>
            )}
          </div>
        );

      case "production":
        return (
          <div className="p-4 space-y-4">
            <h3 className="font-semibold">Production Status</h3>
            {selectedProject.progress !== undefined ? (
              <div className="space-y-4">
                <div className="bg-gray-200 rounded-lg p-4 border border-white/5">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Overall Progress</span>
                    <span className="font-bold">{selectedProject.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className="bg-gradient-to-r from-green-500 to-green-400 h-4 rounded-full transition-all"
                      style={{ width: `${selectedProject.progress}%` }}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg text-sm font-medium transition-colors">
                    + Create Change Order
                  </button>
                  <button className="px-4 py-2 bg-gray-200 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors">
                    View Timeline
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 bg-gray-200 rounded-lg">
                <div className="text-4xl mb-2">🏭</div>
                <div>Not in production yet</div>
              </div>
            )}
          </div>
        );

      case "proofs":
        return (
          <div className="p-4 space-y-4">
            <h3 className="font-semibold">Proofs</h3>
            {selectedProject.proof_status ? (
              <div className="space-y-4">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-yellow-400 font-medium">
                    <span>⏳</span>
                    <span>Awaiting client approval</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Proof sent {selectedProject.proof_sent}</div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors">
                    View Proof PDF
                  </button>
                  <button className="px-4 py-2 bg-gray-200 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors">
                    Resend to Client
                  </button>
                  <button className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-medium transition-colors">
                    Mark Approved
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-200 rounded-lg border border-white/5">
                <div className="text-4xl mb-2">📄</div>
                <div className="text-gray-500 mb-4">No proofs generated yet</div>
                <button className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors">
                  Generate Proof PDF
                </button>
              </div>
            )}
          </div>
        );

      case "notes":
        return (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Notes & Documentation</h3>
              <button className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors">
                + Add Note
              </button>
            </div>
            <div className="bg-gray-200 rounded-lg p-4 border border-white/5">
              {selectedProject.notes ? (
                <p className="text-sm whitespace-pre-wrap">{selectedProject.notes}</p>
              ) : (
                <p className="text-sm text-gray-500 italic">No notes yet</p>
              )}
            </div>
            <div className="text-xs text-gray-500">
              Project created {new Date(selectedProject.created_at).toLocaleString()}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 text-gray-900 flex flex-col">
      {/* Header */}
      <header className="w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-lg">
        <div className="max-w-full mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <h1 className="text-xl font-semibold tracking-wide">3D COLOR</h1>
            <span className="text-xs opacity-80 uppercase">PM Command Center</span>
            <span className="ml-2 px-2 py-0.5 bg-slate-100 rounded text-xs">Option D: Mission Control + Inbox</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 px-3 py-1.5 bg-slate-100 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:border-gray-300"
              />
            </div>
            <button className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors">
              + New Project
            </button>
            <button
              onClick={onBackToApp}
              className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md text-sm font-medium transition-colors"
            >
              Back
            </button>
          </div>
        </div>
      </header>

      {/* Mission Control Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-3 overflow-x-auto">
          <MissionBadge
            id="ships_today"
            icon="🔴"
            count={stats.shipsToday}
            label="Ships Today"
            color="bg-red-600"
            isUrgent={true}
          />
          <MissionBadge
            id="needs_answers"
            icon="⚠️"
            count={stats.needsAnswers}
            label="Need Answers"
            color="bg-yellow-600"
            isUrgent={true}
          />
          <div className="w-px h-10 bg-slate-100" />
          <MissionBadge
            id="intake"
            icon="📥"
            count={stats.intake}
            label="Intake"
            color="bg-blue-600"
          />
          <MissionBadge
            id="prep"
            icon="✍️"
            count={stats.prep}
            label="Prep"
            color="bg-yellow-600"
          />
          <MissionBadge
            id="production"
            icon="🏭"
            count={stats.production}
            label="Production"
            color="bg-green-600"
          />
          <MissionBadge
            id="proofing"
            icon="📄"
            count={stats.proofing}
            label="Proofing"
            color="bg-purple-600"
          />
          <div className="flex-1" />
          {activeFilter && (
            <button
              onClick={() => setActiveFilter(null)}
              className="px-3 py-1.5 bg-gray-200 hover:bg-gray-200 rounded text-sm transition-colors"
            >
              Clear Filter ✕
            </button>
          )}
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Project List */}
        <div className="w-80 border-r border-gray-200 flex flex-col bg-slate-200">
          {/* List Header */}
          <div className="p-3 border-b border-gray-200 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {activeFilter ? (
                <>Filtered: {filteredProjects.length} projects</>
              ) : (
                <>All Projects ({projects.length})</>
              )}
            </span>
          </div>

          {/* Project List */}
          <div className="flex-1 overflow-y-auto">
            {filteredProjects.length > 0 ? (
              filteredProjects.map(renderProjectItem)
            ) : (
              <div className="p-4 text-center text-gray-500">
                No projects match filter
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Workspace */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedProject ? (
            <>
              {/* Project Header */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-lg text-purple-600">
                        {selectedProject.project_id}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${STATUS_CONFIG[selectedProject.status]?.color}`}>
                        {STATUS_CONFIG[selectedProject.status]?.label}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold mt-1">
                      {selectedProject.customer_name}
                    </h2>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${getUrgencyIndicator(selectedProject)?.class || "text-gray-500"}`}>
                      {formatDate(selectedProject.ship_date)}
                    </div>
                    {getUrgencyIndicator(selectedProject) && (
                      <div className={`text-sm ${getUrgencyIndicator(selectedProject).class}`}>
                        {getUrgencyIndicator(selectedProject).icon} {getUrgencyIndicator(selectedProject).label}
                      </div>
                    )}
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mt-4 overflow-x-auto">
                  {WORKSPACE_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
                        activeTab === tab.id
                          ? "bg-slate-100 text-gray-900"
                          : "text-gray-500 hover:text-gray-900 hover:bg-slate-100"
                      }`}
                    >
                      {tab.label}
                      {tab.id === "questions" && selectedProject.questions_pending > 0 && (
                        <span className="ml-1.5 px-1.5 py-0.5 bg-yellow-500 text-black rounded-full text-xs">
                          {selectedProject.questions_pending}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Workspace Content */}
              <div className="flex-1 overflow-y-auto bg-slate-100/20">
                {renderWorkspaceContent()}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              Select a project from the list
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
